import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Pencil, X, ImagePlus, Megaphone, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { uploadFile } from "@/lib/upload";
import { useI18n } from "@/context/I18nContext";
import { Card, Btn, Loader } from "@/components/portal/ui";

const empty = { title: {}, subtitle: {}, image: "", link: "/browse", placement: "hero", active: true, order: 1 };

export function PromotionsManager() {
  const { t, L, lang } = useI18n();
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/admin/promotions");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditing("new"); };
  const openEdit = (p) => { setForm({ ...empty, ...p }); setEditing(p.id); };
  const close = () => { setEditing(null); setForm(empty); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") await api.post("/admin/promotions", form);
      else await api.put(`/admin/promotions/${editing}`, form);
      toast.success(t("common.saved"));
      close();
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    await api.delete(`/admin/promotions/${id}`);
    load();
  };

  const toggle = async (p) => {
    await api.put(`/admin/promotions/${p.id}`, { ...p, active: !p.active });
    load();
  };

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try { const url = await uploadFile(file); setForm((f) => ({ ...f, image: url })); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const st = { background: "var(--bg)", borderColor: "var(--border)", borderRadius: "var(--radius)", color: "var(--text)" };
  if (!items) return <Loader />;

  return (
    <div className="space-y-4" data-testid="promotions-manager">
      <div className="flex items-center justify-between">
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>{items.length} · {t("dash.ads")}</p>
        <Btn onClick={openNew} data-testid="promo-new-btn"><Plus className="h-4 w-4" /> {t("promo.new")}</Btn>
      </div>

      {items.length === 0 && (
        <Card className="p-12 flex flex-col items-center gap-3 text-center" data-testid="promo-empty">
          <Megaphone className="h-8 w-8" style={{ color: "var(--accent)" }} />
          <span style={{ color: "var(--text-muted)" }}>{t("promo.none")}</span>
        </Card>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((p) => (
          <Card key={p.id} className="overflow-hidden kw-fade-up" data-testid={`promo-item-${p.id}`}>
            <div className="h-28 w-full" style={{ background: "var(--surface-alt)" }}>
              {p.image && <img src={p.image} alt="" className="h-full w-full object-cover" />}
            </div>
            <div className="p-4">
              <div className="flex items-center gap-2">
                <span className="font-heading font-bold truncate" style={{ color: "var(--text)" }}>{L(p.title)}</span>
                <span className="ml-auto shrink-0 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full"
                  style={{ background: p.active ? "#DCFCE7" : "var(--surface-alt)", color: p.active ? "#15803D" : "var(--text-muted)" }}>
                  {p.active ? t("promo.active") : t("promo.inactive")}
                </span>
              </div>
              <p className="text-sm mt-1 truncate" style={{ color: "var(--text-muted)" }}>{L(p.subtitle)}</p>
              <div className="mt-3 flex items-center gap-2">
                <Btn variant="soft" onClick={() => openEdit(p)} className="h-9 px-3 text-xs" data-testid={`promo-edit-${p.id}`}><Pencil className="h-3.5 w-3.5" /> {t("promo.edit")}</Btn>
                <Btn variant="ghost" onClick={() => toggle(p)} className="h-9 px-3 text-xs" data-testid={`promo-toggle-${p.id}`}>{p.active ? t("control.suspend") : t("control.activate")}</Btn>
                <Btn variant="danger" onClick={() => remove(p.id)} className="ml-auto h-9 px-3" data-testid={`promo-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Btn>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,.5)" }} data-testid="promo-modal">
          <Card className="w-full max-w-lg p-6 max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-bold" style={{ color: "var(--text)" }}>{editing === "new" ? t("promo.new") : t("promo.edit")}</h3>
              <button onClick={close} data-testid="promo-modal-close"><X className="h-5 w-5" style={{ color: "var(--text-muted)" }} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("promo.title")} ({lang.toUpperCase()})</label>
                <input data-testid="promo-title-input" className="w-full px-3.5 h-11 text-sm border outline-none" style={st}
                  value={form.title?.[lang] || ""} onChange={(e) => setForm({ ...form, title: { ...form.title, [lang]: e.target.value } })} />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("promo.subtitle")} ({lang.toUpperCase()})</label>
                <input data-testid="promo-subtitle-input" className="w-full px-3.5 h-11 text-sm border outline-none" style={st}
                  value={form.subtitle?.[lang] || ""} onChange={(e) => setForm({ ...form, subtitle: { ...form.subtitle, [lang]: e.target.value } })} />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("promo.imageUrl")}</label>
                <div className="mt-2 flex items-center gap-3">
                  <div className="h-16 w-24 overflow-hidden border shrink-0 flex items-center justify-center" style={{ borderColor: "var(--border)", borderRadius: "var(--radius)", background: "var(--surface-alt)" }}>
                    {form.image ? <img src={form.image} alt="" className="h-full w-full object-cover" /> : <ImagePlus className="h-5 w-5" style={{ color: "var(--text-muted)" }} />}
                  </div>
                  <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => doUpload(e.target.files[0])} data-testid="promo-image-file" />
                  <Btn variant="soft" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="promo-image-upload"><Upload className="h-4 w-4" /> {uploading ? t("media.uploading") : t("media.uploadCover")}</Btn>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("promo.link")}</label>
                  <input data-testid="promo-link-input" className="w-full px-3.5 h-11 text-sm border outline-none" style={st}
                    value={form.link || ""} onChange={(e) => setForm({ ...form, link: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("control.status")}</label>
                  <select data-testid="promo-active-input" className="w-full px-3.5 h-11 text-sm border outline-none" style={st}
                    value={form.active ? "1" : "0"} onChange={(e) => setForm({ ...form, active: e.target.value === "1" })}>
                    <option value="1">{t("promo.active")}</option>
                    <option value="0">{t("promo.inactive")}</option>
                  </select>
                </div>
              </div>
              <Btn onClick={save} disabled={saving || !form.title?.[lang]} className="w-full" data-testid="promo-save-btn">{t("common.save")}</Btn>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
